import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { Icon } from '@lomarengas/design-system';
import { DeleteConfirmation } from '../../../common/components';
import { useAppDispatch } from '../../../app/hooks';
import { deleteSeasonRateAsync, SeasonRateDetails } from '../seasonRatesSlice';
import { WEEKDAYS_SHORT } from '../../../common/constants';

interface SeasonRateListItemProps {
  AccommodationName: string;
  AccommodationId: string;
  SeasonRateItem: SeasonRateDetails;
  hasWriteAccess: boolean;
  selectable?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string, checked: boolean) => void;
}

const SeasonRateListItem = ({
  AccommodationName,
  AccommodationId,
  SeasonRateItem,
  hasWriteAccess,
  selectable = false,
  isSelected = false,
  onToggleSelect,
}: SeasonRateListItemProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [deleteId, setDeleteId] = useState(null);
  const [deleteDisplayConfirmationModal, setDeleteDisplayConfirmationModal] =
    useState(false);
  const [deleteMessage, setDeleteMessage] = useState(null);

  const seasonRateEditUrl = `/rates/updateseasonrate/${AccommodationId}/${
    SeasonRateItem.id
  }?accommodation=${encodeURI(AccommodationName)}`;

  const showDeleteModal = () => {
    setDeleteId(SeasonRateItem.id);

    setDeleteMessage(
      `Haluatko varmasti poistaa hintakauden '${SeasonRateItem.startDateString} - ${SeasonRateItem.endDateString} ${SeasonRateItem.seasonName}'?`,
    );

    setDeleteDisplayConfirmationModal(true);
  };

  const hideDeleteModal = () => {
    setDeleteDisplayConfirmationModal(false);
  };

  const deleteSeasonRate = id => {
    dispatch(deleteSeasonRateAsync(id));
    setDeleteDisplayConfirmationModal(false);
  };

  const getPreparationBadgeClass = (days: number) => {
    if (days === 0) {
      return 'bg-danger';
    }
    if (days === 1) {
      return 'bg-warning';
    }
    return 'bg-success';
  };

  const renderRatePrices = SeasonRateItem.ratePrices.map(price => (
    <span key={price.duration}>
      <span className="fw-medium">{price.duration} vrk: </span>
      {price.discountedPrice ? (
        <>
          <span
            style={{ textDecoration: 'line-through', paddingRight: '0.5rem' }}
          >
            {price.dailyPrice}
          </span>
          {price.discountedPrice} €
        </>
      ) : (
        `${price.dailyPrice} €`
      )}
      {price.duration === 7 && (
        <div>
          <span className="fw-medium">Lisävrk: </span>
          {SeasonRateItem.discountedPriceForAdditionalDay ||
            SeasonRateItem.priceForAdditionalDay}{' '}
          €
        </div>
      )}
    </span>
  ));

  return (
    <>
      <tr
        style={{
          backgroundColor: SeasonRateItem.seasonRateColorHex,
          padding: '0',
          margin: '0',
          borderBottom: '0',
        }}
      >
        <td style={{ padding: '0.75rem', margin: '0', width: '56px' }}>
          {selectable && (
            <input
              type="checkbox"
              aria-label="Valitse kausi"
              checked={isSelected}
              onChange={e =>
                onToggleSelect &&
                onToggleSelect(SeasonRateItem.id, e.target.checked)
              }
              onClick={e => e.stopPropagation()}
              style={{
                transform: 'scale(1.4)',
                transformOrigin: 'center',
                cursor: 'pointer',
              }}
            />
          )}
        </td>
        <td style={{ padding: '0.75rem', margin: '0' }}>
          <Link to={seasonRateEditUrl} className="d-block">
            <strong>{SeasonRateItem.seasonName}</strong>
          </Link>
        </td>
        <td style={{ padding: '0.75rem', margin: '0' }}>
          <Link to={seasonRateEditUrl} className="d-block">
            {SeasonRateItem.startDateString} - {SeasonRateItem.endDateString}
          </Link>
        </td>
        <td style={{ padding: '0.75rem', margin: '0' }}>
          {SeasonRateItem.preparationDays !== null &&
            SeasonRateItem.preparationDays >= 0 && (
              <span
                className={`badge ${getPreparationBadgeClass(
                  SeasonRateItem.preparationDays,
                )} ms-2`}
              >
                <span>Varoaika: </span>
                {SeasonRateItem.preparationDays}
              </span>
            )}
          {SeasonRateItem.minBookingDuration > 1 && (
            <div>
              <span>Min. varausaika: </span>
              {SeasonRateItem.minBookingDuration}
            </div>
          )}
          {SeasonRateItem.releaseInterval && (
            <div>
              <span>Vapautus vrk: </span>
              {SeasonRateItem.releaseInterval}
            </div>
          )}
        </td>
        <td style={{ padding: '0.75rem', margin: '0' }}>
          {SeasonRateItem.lastMinuteDaysUntilStart && (
            <div>
              <span>Vrk loman alkuun: </span>
              {SeasonRateItem.lastMinuteDaysUntilStart}
            </div>
          )}
          {SeasonRateItem.lastMinuteDiscount && (
            <div>
              <span>Hinnan muutos: </span>
              {SeasonRateItem.lastMinuteDiscount}
            </div>
          )}
          <div>
            {SeasonRateItem.mandatoryAddonNames.map(addon => (
              <div key={addon}>{addon}</div>
            ))}
          </div>
        </td>

        <td style={{ padding: '0.75rem', margin: '0' }}>
          <div className="d-flex gap-16">
            {SeasonRateItem.restrictedCheckInWeekdays.length > 0 && (
              <div>
                <div className="fw-medium">Sisäänk.</div>
                {WEEKDAYS_SHORT.filter(
                  weekday =>
                    !SeasonRateItem.restrictedCheckInWeekdays.includes(
                      weekday.weekdayInt,
                    ),
                ).map((weekday, index, array) => (
                  <span key={weekday.weekdayInt}>
                    {weekday.weekdayString}
                    {index < array.length - 1 && ', '}
                  </span>
                ))}
              </div>
            )}
            {SeasonRateItem.restrictedCheckOutWeekdays.length > 0 && (
              <div>
                <div className="fw-medium">Ulosk.</div>
                {WEEKDAYS_SHORT.filter(
                  weekday =>
                    !SeasonRateItem.restrictedCheckOutWeekdays.includes(
                      weekday.weekdayInt,
                    ),
                ).map((weekday, index, array) => (
                  <div key={weekday.weekdayInt}>
                    {weekday.weekdayString}
                    {index < array.length - 1 && ','}
                  </div>
                ))}
              </div>
            )}
          </div>
        </td>
        <td style={{ padding: '0.75rem', margin: '0' }}>
          <table>
            <tbody>
              <tr>
                <td
                  style={{
                    verticalAlign: 'top',
                    padding: '0.75rem',
                    margin: '0',
                  }}
                >
                  {SeasonRateItem.weekdayPriceChangeRules.length > 0 && (
                    <div>
                      {SeasonRateItem.weekdayPriceChangeRules.map(rule => {
                        const weekday = WEEKDAYS_SHORT.find(
                          w => w.weekdayInt === rule.weekday,
                        );
                        return (
                          <div key={rule.weekday}>
                            {weekday?.weekdayString}: {rule.priceChange}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </td>
        <td
          className="table-data-td-actions"
          style={{ padding: '0', margin: '0', width: 'auto' }}
          rowSpan={2}
        >
          <Dropdown>
            <Dropdown.Toggle
              variant="link"
              id={`dropdown-${SeasonRateItem.id}`}
              className="table-data-btn-actions"
            >
              <Icon AriaLabel="Toiminnot" Name="three-dots-vertical" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => navigate(seasonRateEditUrl)}>
                Muokkaa hintakautta
              </Dropdown.Item>
              {hasWriteAccess && (
                <Dropdown.Item onClick={() => showDeleteModal()}>
                  Poista hintakausi
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </td>
      </tr>
      <tr
        style={{
          backgroundColor: SeasonRateItem.seasonRateColorHex,
          padding: '0',
          margin: '0',
        }}
      >
        <td
          colSpan={7}
          style={{
            paddingLeft: '0.75rem',
            paddingBottom: '0.75rem',
            paddingRight: '0.75rem',
            paddingTop: '0',
            margin: '0',
          }}
        >
          <div className="d-flex gap-24 bg-white p-8 rounded bg-opacity-50">
            {renderRatePrices}
          </div>
        </td>
      </tr>
      {deleteDisplayConfirmationModal && (
        <DeleteConfirmation
          ShowModal={deleteDisplayConfirmationModal}
          ConfirmDelete={deleteSeasonRate}
          HideModal={hideDeleteModal}
          Id={deleteId}
          Message={deleteMessage}
        />
      )}
    </>
  );
};

export default SeasonRateListItem;
export { SeasonRateListItem };
export type { SeasonRateListItemProps };
